import type { SchemaFieldType } from "@erp/shared-types";

/**
 * Legacy import name for scalar array items parsed from JSON Schema
 * (`items: { type: "string" }`). New schemas should use `ARRAY_ITEM_FIELD_NAME`.
 */
export const SCALAR_ARRAY_VALUE_FIELD_NAME = "value" as const;

/**
 * Canonical editor name for a lone scalar/array-item child (not present in extracted JSON).
 */
export const ARRAY_ITEM_FIELD_NAME = "item" as const;

export type ArrayItemMode = "untyped" | "scalar" | "object";

const ARRAY_ITEM_PLACEHOLDER_NAMES = new Set([
  SCALAR_ARRAY_VALUE_FIELD_NAME,
  ARRAY_ITEM_FIELD_NAME,
  "item",
]);

export function isArrayItemPlaceholderName(fieldName: string): boolean {
  return ARRAY_ITEM_PLACEHOLDER_NAMES.has(fieldName);
}

export function flattenArrayObjectWrapper<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): ArrayFieldLike<TChild> {
  if (field.fieldType !== "array") {
    return field;
  }

  const children = field.children ?? [];
  if (children.length === 1 && children[0].fieldType === "object") {
    return {
      ...field,
      children: children[0].children as TChild[],
    };
  }

  return field;
}

export function normalizeArrayFieldForEditor<
  TChild extends ArrayFieldChildLike,
>(field: ArrayFieldLike<TChild>): ArrayFieldLike<TChild> {
  const flattened = flattenArrayObjectWrapper(field);
  const children = flattened.children ?? [];

  if (children.length !== 1) {
    return flattened;
  }

  const onlyChild = children[0];
  if (
    isArrayItemPlaceholderName(onlyChild.fieldName) &&
    onlyChild.fieldType !== "object" &&
    onlyChild.fieldType !== "array"
  ) {
    return {
      ...flattened,
      children: [
        {
          ...onlyChild,
          fieldName: ARRAY_ITEM_FIELD_NAME,
        },
      ] as TChild[],
    };
  }

  return flattened;
}

export function getArrayItemMode<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): ArrayItemMode {
  if (field.fieldType !== "array") {
    return "untyped";
  }

  const normalized = normalizeArrayFieldForEditor(field);
  const children = normalized.children ?? [];

  if (children.length === 0) {
    return "untyped";
  }
  if (children.length >= 2) {
    return "object";
  }

  const onlyChild = children[0];
  if (onlyChild.fieldType === "object" || onlyChild.fieldType === "array") {
    return onlyChild.fieldType === "object" ? "object" : "scalar";
  }

  return "scalar";
}

export function getScalarArrayItemType<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): SchemaFieldType | null {
  if (getArrayItemMode(field) !== "scalar") {
    return null;
  }

  const normalized = normalizeArrayFieldForEditor(field);
  return normalized.children?.[0]?.fieldType ?? null;
}

export function shouldHideArrayItemChildRow<TChild extends ArrayFieldChildLike>(
  parent: ArrayFieldLike<TChild> | null | undefined,
  field: ArrayFieldChildLike,
): boolean {
  if (!parent || parent.fieldType !== "array") {
    return false;
  }

  const normalized = normalizeArrayFieldForEditor(parent);
  const children = normalized.children ?? [];

  if (children.length !== 1) {
    return false;
  }

  const onlyChild = children[0];
  if (onlyChild.fieldName !== field.fieldName) {
    return false;
  }

  if (
    onlyChild.fieldType === "object" &&
    (onlyChild.children?.length ?? 0) > 0
  ) {
    return true;
  }

  return (
    isArrayItemPlaceholderName(onlyChild.fieldName) &&
    onlyChild.fieldType !== "object"
  );
}

export const ARRAY_SCALAR_ITEM_TYPES = [
  "string",
  "text",
  "integer",
  "float",
  "boolean",
  "date",
  "datetime",
  "json",
  "array",
] as const satisfies readonly SchemaFieldType[];

export type ArrayScalarItemType = (typeof ARRAY_SCALAR_ITEM_TYPES)[number];

export type ArrayItemTypeSelection = "untyped" | ArrayScalarItemType | "object";

export function getArrayItemTypeSelection<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): ArrayItemTypeSelection {
  const mode = getArrayItemMode(field);
  if (mode === "untyped") return "untyped";
  if (mode === "object") return "object";
  const scalarType = getScalarArrayItemType(field);
  if (
    scalarType &&
    (ARRAY_SCALAR_ITEM_TYPES as readonly string[]).includes(scalarType)
  ) {
    return scalarType as ArrayScalarItemType;
  }
  return "untyped";
}

export function setArrayItemTypeSelection<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
  selection: ArrayItemTypeSelection,
): ArrayFieldLike<TChild> {
  if (field.fieldType !== "array") {
    return field;
  }

  if (selection === "untyped") {
    return { ...field, children: [] };
  }

  if (selection === "object") {
    const existing = normalizeArrayFieldForEditor(field).children ?? [];
    if (existing.length >= 2) {
      return { ...field, children: existing as TChild[] };
    }
    if (
      existing.length === 1 &&
      existing[0].fieldType === "object" &&
      (existing[0].children?.length ?? 0) > 0
    ) {
      return { ...field, children: existing[0].children as TChild[] };
    }
    return { ...field, children: existing as TChild[] };
  }

  return {
    ...field,
    children: [
      {
        fieldName: ARRAY_ITEM_FIELD_NAME,
        fieldType: selection,
      } as TChild,
    ],
  };
}

export function getVisibleArrayChildren<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): TChild[] {
  if (field.fieldType !== "array") {
    return (field.children ?? []) as TChild[];
  }

  const normalized = normalizeArrayFieldForEditor(field);
  const children = (normalized.children ?? []) as TChild[];

  return children.filter((child) => !shouldHideArrayItemChildRow(field, child));
}

type ArrayFieldChildLike = {
  fieldName: string;
  fieldType: SchemaFieldType;
  children?: ArrayFieldChildLike[];
};

type ArrayFieldLike<TChild extends ArrayFieldChildLike = ArrayFieldChildLike> =
  {
    fieldType: SchemaFieldType;
    children?: TChild[];
  };

export function isContainerFieldType(fieldType: SchemaFieldType): boolean {
  return fieldType === "object" || fieldType === "array";
}

export function isScalarArrayValueField(field: ArrayFieldChildLike): boolean {
  return (
    field.fieldName === SCALAR_ARRAY_VALUE_FIELD_NAME &&
    field.fieldType !== "object" &&
    field.fieldType !== "array"
  );
}

function formatChildTypeLabel(child: ArrayFieldChildLike): string {
  if (child.fieldType === "array" || child.fieldType === "object") {
    return formatFieldTypeLabel(child);
  }
  return child.fieldType;
}

/**
 * Human-readable type label. Array/object children define item/property shapes
 * recursively (mirrors `buildExtendExtractionConfig` semantics).
 */
export function formatFieldTypeLabel<TChild extends ArrayFieldChildLike>(
  field: ArrayFieldLike<TChild>,
): string {
  if (field.fieldType === "array") {
    const children = field.children ?? [];
    if (children.length === 0) {
      return "array";
    }
    if (children.length === 1) {
      return `array<${formatChildTypeLabel(children[0])}>`;
    }
    const props = children.map((child) => child.fieldName).join(", ");
    return `array<object{${props}}>`;
  }

  if (field.fieldType === "object") {
    const children = field.children ?? [];
    if (children.length === 0) {
      return "object";
    }
    const props = children
      .map((child) => `${child.fieldName}: ${formatChildTypeLabel(child)}`)
      .join(", ");
    return `object{${props}}`;
  }

  return field.fieldType;
}

/**
 * Whether the array field can accept another direct child at this level.
 * - 0 children → define item schema
 * - 2+ children → object-item shorthand (add properties)
 * - 1 child → item fully defined (edit nested child instead)
 */
export function canAddDirectChildToArrayField<
  TChild extends ArrayFieldChildLike,
>(field: ArrayFieldLike<TChild>): boolean {
  if (field.fieldType !== "array") {
    return false;
  }

  const mode = getArrayItemMode(field);
  return mode === "untyped" || mode === "object";
}

/** Hides the key editor for a lone scalar/array-item child (name is not in extracted JSON). */
export function isAnonymousArrayItemChild(
  parent: ArrayFieldLike | null | undefined,
  field: ArrayFieldChildLike,
): boolean {
  return shouldHideArrayItemChildRow(parent, field);
}
